import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import {
  isStepUp,
  resolveAgentText,
  type ActionKind,
  type AnyScreenAction,
  type Navigation,
  type Permission,
  type ScreenManifest,
  type ScreenRegistry,
} from '@/shared/screen';

import { jsonSafe, toolData, toolFailure, type AgentTool } from './agent-tool';
import { schemaOf } from './from-manifest';
import { isManualOnlyScreen } from './manual-only';

/** O que o Luy executa na tela aberta (U56, L03): consultar, navegar e preparar rascunho. */
export const RUNNABLE_KINDS: ReadonlySet<ActionKind> = new Set<ActionKind>(['read', 'navigate', 'draft']);

/** Chave do resultado com o total de campos preenchidos; o chat lê dela o "Preencheu N campos". */
const FILLED_KEY = 'filled';

export interface ScreenActionDeps {
  readonly registry: ScreenRegistry;
  readonly i18n: I18n;
  readonly nav: Navigation;
  readonly visible: (screenId: string) => boolean;
  readonly canUse: (permission: Permission | undefined) => boolean;
  /** A tela montada e acessível agora. */
  readonly activeScreen: () => string | null;
  /** O manifesto que a tela montada registrou, com os `run` de verdade (o padrão `mountedScreenRequired`). */
  readonly mountedScreen: (screenId: string) => ScreenManifest | undefined;
}

/* eslint-disable lingui/no-unlocalized-strings -- respostas ao modelo, inglês canônico (U33) */
type Gate = { readonly ok: true } | { readonly ok: false; readonly reason: string };

/** As travas fixas da U56 por ação: kind, step-up, `agent: false`, permissão e alvo da ação de linha. */
function screenActionGate(action: AnyScreenAction, canUse: (permission: Permission | undefined) => boolean): Gate {
  if (!RUNNABLE_KINDS.has(action.kind) || isStepUp(action))
    return { ok: false, reason: `${action.kind} actions are done by the person in the app (saving, sending and approving are never run by the assistant).` };
  if (action.agent === false) return { ok: false, reason: 'This action is manual only.' };
  if (!canUse(action.permission)) return { ok: false, reason: 'The person does not have permission for this action.' };
  if (action.row === true && Object.keys((schemaOf(action)['properties'] as Record<string, unknown> | undefined) ?? {}).length === 0)
    return { ok: false, reason: 'Row action without a target in its input.' };
  return { ok: true };
}

const NOT_MOUNTED = 'Not available in the current state of the screen.';

function issuesOf(error: z.ZodError): string {
  return error.issues.map((issue) => `${issue.path.join('.') || '(input)'}: ${issue.message}`).join('; ');
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : 'The action failed in the browser.';
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** Total de campos que a ação preencheu, quando ela devolve `{ applied, skipped, errors }` (L2). */
function filledOf(value: unknown): number | undefined {
  if (!isRecord(value) || !Array.isArray(value['applied']) || !Array.isArray(value['skipped'])) return undefined;
  return value['applied'].length;
}

const FILLED = new RegExp(`"${FILLED_KEY}":(\\d+)`);
const EMPTY_RESULT = /"result":\[\]/;

/** O "Preencheu N campos" a partir da saída do `uyzy_run_screen_action` (também a cortada pelo motor). */
export function filledCountOf(output: string | null | undefined): number | null {
  const match = output ? FILLED.exec(output) : null;
  return match ? Number(match[1]) : null;
}

/** A busca que não achou nada (`result: []`), para o chat dizer "Nenhum produto encontrado". */
export function emptyResultOf(output: string | null | undefined): boolean {
  return output ? EMPTY_RESULT.test(output) : false;
}
/* eslint-enable lingui/no-unlocalized-strings */

const NOTES = {
  here: (title: string, screenId: string, purpose: string) => msg`A pessoa está vendo agora a tela ${title} (screenId ${screenId}). ${purpose}`,
  can: (labels: string) => msg`Nela você pode: ${labels}.`,
  short: (title: string, screenId: string) => msg`Tela aberta agora: ${title} (screenId ${screenId}). Para pedido sobre ela, use get_screen_actions direto, sem listar nem abrir telas.`,
  cannot: msg`Nela não há ação que você execute agora; quem conclui é a pessoa, pelos botões da tela.`,
  start: msg`Se o pedido for sobre a tela que a pessoa está vendo, comece por get_screen_actions (ou get_screen_guide sem screenId): não precisa abrir nem listar telas.`,
  none: msg`Nenhuma tela que você possa operar está aberta agora: para agir numa tela, abra-a antes com open_screen.`,
};

/** A primeira frase da descrição da tela: o "para que serve" da nota do turno. */
function firstSentence(text: string): string {
  const [first] = text.trim().split(/(?<=[.!?])\s+/);
  return first ?? '';
}

/** A nota do turno (U56): a tela que a pessoa vê agora, o que dá para fazer nela e por onde começar. */
export interface ScreenTurnNotes {
  /** Para o get_screen_actions e o get_screen_guide. */
  readonly full: string;
  /** Uma linha, para o list_screens e o open_screen. */
  readonly short: string;
}

const DESCRIPTIONS = {
  list: msg`Lista as ações da tela aberta agora que você pode usar com run_screen_action: consultar, filtrar, navegar e preparar rascunhos (preencher campos). Traz o JSON Schema do input de cada uma e se ela está disponível agora. Gravar, enviar e aprovar não aparecem: são da pessoa.`,
  run: msg`Executa na tela aberta uma ação de get_screen_actions: passe o actionId e o input dela. Só consulta, navega ou prepara rascunho: nunca grava, envia nem aprova. Devolve o resultado da ação; num preenchimento, o que entrou (applied), o que foi pulado porque a pessoa já tinha editado (skipped) e os erros de validação (errors).`,
};

type OpenScreen = { readonly screen: ScreenManifest; readonly mounted: ScreenManifest | undefined } | { readonly failure: string };

/* eslint-disable lingui/no-unlocalized-strings -- respostas ao modelo, inglês canônico (U33) */
/** A tela montada que o Luy pode operar agora, ou o motivo de não poder. */
function openScreen(deps: ScreenActionDeps): OpenScreen {
  const id = deps.activeScreen();
  if (id === null) return { failure: 'No screen is open. Open one with uyzy_open_screen first.' };
  const mounted = deps.mountedScreen(id);
  const screen = deps.registry.byId(id) ?? mounted;
  if (!screen || screen.agent === false || mounted?.agent === false || !deps.visible(id) || isManualOnlyScreen(deps.registry, screen))
    return { failure: `Screen ${id} is manual only: login, MFA, step-up and Security are never operated by the assistant.` };
  return { screen, mounted };
}
/* eslint-enable lingui/no-unlocalized-strings */

/** As ações da tela: as do manifesto, na ordem dele, mais as que só a tela montada registra. */
function actionsOf(screen: ScreenManifest, mounted: ScreenManifest | undefined): AnyScreenAction[] {
  const known = new Set(screen.actions.map((action) => action.id));
  return [...screen.actions, ...(mounted?.actions ?? []).filter((action) => !known.has(action.id))];
}

/** As ações que o Luy executa agora na tela: passam pelas travas e a tela montada registrou. */
function runnableNow(deps: ScreenActionDeps, screen: ScreenManifest, mounted: ScreenManifest | undefined): AnyScreenAction[] {
  return actionsOf(screen, mounted).filter((action) => screenActionGate(action, deps.canUse).ok && mounted?.actions.some((item) => item.id === action.id) === true);
}

/** A nota do turno a partir da tela montada, a mesma fonte do get_screen_actions e do get_screen_guide (U56). */
export function screenTurnNotes(deps: ScreenActionDeps): ScreenTurnNotes {
  const found = openScreen(deps);
  if ('failure' in found) {
    const none = deps.i18n._(NOTES.none);
    return { full: none, short: none };
  }
  const { screen, mounted } = found;
  const title = deps.i18n._(screen.title);
  const screenId = screen.id;
  const purpose = firstSentence(resolveAgentText(deps.i18n, screen.description));
  const labels = runnableNow(deps, screen, mounted)
    .map((action) => deps.i18n._(action.label))
    .join(', ');
  const can = deps.i18n._(labels === '' ? NOTES.cannot : NOTES.can(labels));
  return {
    full: [deps.i18n._(NOTES.here(title, screenId, purpose)).trim(), can, deps.i18n._(NOTES.start)].join(' '),
    short: deps.i18n._(NOTES.short(title, screenId)),
  };
}

/** `get_screen_actions` e `run_screen_action` (U56, L1): só para o Luy, nunca no WebMCP do navegador (`internal`). */
export function screenActionTools(deps: ScreenActionDeps): AgentTool[] {
  const open = () => openScreen(deps);

  const list = (): AgentTool => ({
    name: 'get_screen_actions',
    description: deps.i18n._(DESCRIPTIONS.list),
    inputSchema: z.toJSONSchema(z.strictObject({})),
    readOnly: true,
    scope: 'app',
    origin: {},
    internal: true,
    turnNote: () => screenTurnNotes(deps).full,
    execute: () => {
      const found = open();
      if ('failure' in found) return Promise.resolve(toolFailure(found.failure));
      const { screen, mounted } = found;
      const actions = actionsOf(screen, mounted).flatMap((action) => {
        if (!screenActionGate(action, deps.canUse).ok) return [];
        const available = mounted?.actions.some((item) => item.id === action.id) === true;
        return [
          {
            actionId: action.id,
            label: deps.i18n._(action.label),
            description: resolveAgentText(deps.i18n, action.description),
            kind: action.kind,
            inputSchema: schemaOf(action),
            available,
            ...(available ? {} : { reason: NOT_MOUNTED }),
            ...(action.row === true ? { row: true } : {}),
            ...(action.page === true ? { page: true } : {}),
          },
        ];
      });
      return Promise.resolve(toolData({ screenId: screen.id, title: deps.i18n._(screen.title), actions }));
    },
  });

  const runInput = z.strictObject({ actionId: z.string().min(1), input: z.record(z.string(), z.unknown()).optional() });
  const run = (): AgentTool => ({
    name: 'run_screen_action',
    description: deps.i18n._(DESCRIPTIONS.run),
    inputSchema: z.toJSONSchema(runInput),
    readOnly: true,
    scope: 'app',
    origin: {},
    internal: true,
    execute: async (raw, options) => {
      const parsed = runInput.safeParse(raw);
      if (!parsed.success) return toolFailure(`Invalid input: ${issuesOf(parsed.error)}`);
      const { actionId } = parsed.data;
      const found = open();
      if ('failure' in found) return toolFailure(found.failure);
      const { screen, mounted } = found;
      const declared = actionsOf(screen, mounted).find((action) => action.id === actionId);
      if (!declared) return toolFailure(`Action ${actionId} is not on the open screen (${screen.id}). Call uyzy_get_screen_actions.`);
      const gate = screenActionGate(declared, deps.canUse);
      if (!gate.ok) return toolFailure(`Action ${actionId} refused: ${gate.reason}`);
      const action = mounted?.actions.find((item) => item.id === actionId);
      if (!action) return toolFailure(`Action ${actionId}: ${NOT_MOUNTED} The screen ${screen.id} must be open with it.`);
      const input = (action.input ?? z.strictObject({})).safeParse(parsed.data.input ?? {});
      if (!input.success) return toolFailure(`Invalid input for ${actionId}: ${issuesOf(input.error)}`);
      if (options?.signal?.aborted) return toolFailure('Cancelled before running the action.');
      const label = deps.i18n._(action.label);
      let returned: unknown;
      try {
        returned = await action.run({ input: input.data as Record<string, unknown>, nav: deps.nav });
      } catch (error) {
        return toolFailure(`${label}: ${messageOf(error)}`);
      }
      const result = jsonSafe(returned) ?? null;
      const filled = filledOf(result);
      return toolData({ actionId, ...(filled === undefined ? {} : { [FILLED_KEY]: filled }), label, kind: action.kind, result });
    },
  });
  return [list(), run()];
}
